import * as React from "react";
import { useState } from "react";
import { usePopper } from "react-popper";
import { useTranslation } from "react-i18next";
import ReactDOM from "react-dom";
import { ThemeColorVariable, ThemeIconSizeVariable } from "@caspeco/casper-ui-library.base-ui.theme";
import { Icon, Icons } from "@caspeco/casper-ui-library.components.icon";
import { StyledPopover, StyledPopoverTrigger } from "./Popover.styles";

interface IPopoverProps {
	/** Required id. */
	id: string;
	/** Content shown in popover. */
	content: React.ReactNode;
	/** At what distance from the trigger element the Popover should appear. Defaults to 0. */
	distanceFromTriggerElement?: number;
	/** Function to be called on toggle. */
	onToggle?: () => void;
	/** Trigger element. */
	children: React.ReactNode;
}

function Popover({ id, children, content, distanceFromTriggerElement = 0, onToggle }: IPopoverProps) {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
	const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
	const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null);

	// Setup popper. See docs at https://popper.js.org/docs/v2/
	const { styles, attributes } = usePopper(referenceElement, popperElement, {
		modifiers: [
			{
				name: "arrow",
				options: {
					element: arrowElement,
				},
			},
			{ name: "offset", options: { offset: [0, distanceFromTriggerElement] } },
		],
		strategy: "fixed",
	});

	const togglePopover = () => {
		if (onToggle) onToggle();
		setIsOpen(!isOpen);
	};

	const closeButton = (
		<button type="button" onClick={togglePopover} id="popover-closebutton" aria-label={t("action_close")}>
			<Icon icon={Icons.Close} size={ThemeIconSizeVariable.Small} color={ThemeColorVariable.Surface} />
		</button>
	);
	const popoverContent = ReactDOM.createPortal(
		<StyledPopover
			role="tooltip"
			id={`${id}-popover`}
			ref={setPopperElement}
			style={styles.popper}
			{...attributes.popper}
		>
			<div id="arrow" ref={setArrowElement} style={styles.arrow} data-popper-arrow />
			{closeButton}
			{content}
		</StyledPopover>,
		document.body,
	);
	return (
		<>
			<StyledPopoverTrigger
				id={id}
				aria-describedby={`${id}-popover`}
				ref={setReferenceElement}
				onClick={togglePopover}
			>
				{children}
			</StyledPopoverTrigger>
			{isOpen && popoverContent}
		</>
	);
}
export default Popover;
